// Skill-Vorschlag bei natuerlicher Sprache (B): erkennt Schluesselwoerter in
// einem normalen Chat-Text und schlaegt den passenden Skill als Button vor.
// NIE automatisch ausfuehren — der Nutzer klickt aktiv.
// Reine Funktion — kein IO, kein React, kein LLM.
//
// Caller stellt sicher: Text beginnt NICHT mit "/" und enthaelt keinen
// Inline-Befehl (sonst greift die Inline-Normalisierung in proceedWithSend).

export type SuggestableSkill =
  | 'presentation'
  | 'research'
  | 'linkedin'
  | 'x'
  | 'newsletter'
  | 'blog'
  | 'image'
  | 'data-analysis'

export interface SkillIntent {
  skill: SuggestableSkill
  label: string
}

interface KeywordRule {
  skill: SuggestableSkill
  label: string
  /** Muster mit Wortgrenzen; Umlaute explizit, case-insensitive. */
  patterns: RegExp[]
}

// \b funktioniert nicht zuverlaessig mit Umlauten (ä/ö/ü gelten als Wortzeichen,
// aber ein Muster wie /präsi\b/ matcht "präsi" in "Präsident" nicht — hier
// bewusst so gewollt). Mehrwort-Keywords werden als Phrase mit \b an beiden
// Enden gefuehrt.
const RULES: KeywordRule[] = [
  {
    skill: 'presentation',
    label: 'Als Präsentation erstellen',
    patterns: [
      /\bpr[äa]si\b/i,
      /\bpr[äa](?:e)?sentation(?:en)?\b/i,
      /\bfoliensatz\b/i,
      /\bfolien\b/i,
      /\bslides?\b/i,
      /\bslide\s*deck\b/i,
      /\bpresentation\b/i,
    ],
  },
  {
    skill: 'research',
    label: 'Als Recherche starten',
    patterns: [/\brecherche\b/i, /\brecherchier(e|en)\b/i, /\bresearch\b/i],
  },
  {
    skill: 'linkedin',
    label: 'Als LinkedIn-Beitrag erstellen',
    patterns: [/\blinkedin[-\s]?post\b/i, /\blinkedin[-\s]?beitrag\b/i, /\blinkedin\b/i],
  },
  {
    skill: 'x',
    label: 'Als X-Beitrag erstellen',
    patterns: [/\bx[-\s]?post\b/i, /\btweet\b/i],
  },
  {
    skill: 'newsletter',
    label: 'Als Newsletter erstellen',
    patterns: [/\bnewsletter\b/i],
  },
  {
    skill: 'blog',
    label: 'Als Blog-Artikel erstellen',
    patterns: [
      /\bblog[-\s]?artikel\b/i,
      /\bblog[-\s]?beitrag\b/i,
      /\bblog[-\s]?post\b/i,
    ],
  },
  {
    skill: 'image',
    label: 'Als Bild generieren',
    patterns: [
      /\bbild\s+generieren\b/i,
      /\berstelle\s+ein\s+bild\b/i,
      /\bgeneriere\s+ein\s+bild\b/i,
      /\billustration\b/i,
    ],
  },
  {
    skill: 'data-analysis',
    label: 'Als Datenanalyse starten',
    patterns: [/\bdatenanalyse\b/i, /\banalysiere\s+die\s+daten\b/i, /\bdata\s+analysis\b/i],
  },
]

/**
 * Liefert den Skill-Vorschlag fuer einen natuerlichsprachlichen Text oder
 * null. Bei mehreren Treffern gewinnt die frueheste Position im Text.
 */
export function detectSkillIntent(text: string): SkillIntent | null {
  const trimmed = text.trim()
  if (!trimmed) return null
  // Reiner Slash-Befehl oder Inline-Befehl -> kein Vorschlag (Caller prueft,
  // hier defensiv nochmal gegen Textanfang mit "/").
  if (trimmed.startsWith('/')) return null

  // Wiki-Links und URLs sind Verweise, keine Absicht: durch gleich lange
  // Leerzeichen ersetzen, damit Positionen fuer "frueheste" stabil bleiben.
  const searchable = trimmed
    .replace(/\[\[[^\]]*\]\]/g, (m) => ' '.repeat(m.length))
    .replace(/https?:\/\/\S+/gi, (m) => ' '.repeat(m.length))

  let best: { rule: KeywordRule; index: number } | null = null
  for (const rule of RULES) {
    for (const pattern of rule.patterns) {
      pattern.lastIndex = 0
      const match = pattern.exec(searchable)
      if (!match) continue
      const index = match.index
      if (!best || index < best.index) {
        best = { rule, index }
      }
    }
  }

  if (!best) return null
  return { skill: best.rule.skill, label: best.rule.label }
}
